/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useMemo, useState, useRef, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { request } from "service/request";
import { endPoints, apiMethods } from "service/endpoints";
import useOnClickOutside from "helpers/useOnClickOutside";
import { useDispatch } from "react-redux";
import { ChevronDownIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import RadioButtonUI from "UI/RadioButton";
import CapsuleBox from "UI/CapsuleElement";
import CommonAccordion from "../Accordion";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";

export default function ExamsDropdown({
  dropdownData = {},
  examName,
  examId,
  callMetaApi,
}) {
  const menuRef = useRef();
  const history = useHistory();
  const dispatch = useDispatch();

  const [selectedExamId, setSelectedExamId] = useState(examId);

  const [show, setShow] = useState(false);

  useOnClickOutside(menuRef, () => {
    setShow(false);
  });

  const examsHeadline = useMemo(() => {
    return Object.keys(dropdownData);
  }, [dropdownData]);

  const switchExam = async (examId) => {
    const results = await request({
      url: `${endPoints.user.SWITCH_EXAM}`,
      method: apiMethods.POST,
      showToast: false,
      isLoader: false,
      data: {
        exam_id: examId,
      },
    });
    if (results.success) {
      window.open("/portal/dashboard", "_self");
      callMetaApi();
      dispatch({ type: "TOGGLE_SWITCH_EXAM" });
      setSelectedExamId(examId);
    }
  };

  const handleShowDropdown = () => {
    setShow(!show);
  };

  const handleChooseExam = (exam) => {
    switchExam(exam.id);
    setShow(!show);
  };

  useEffect(() => {
    setSelectedExamId(examId);
  }, [examId]);

  return (
    <div ref={menuRef} className="relative cursor-pointer">
      <CapsuleBox
        parentClass={`group flex items-center gap-2 py-1 pl-1 pr-2 rounded-3xl ${
          show
            ? "bg-primary text-white"
            : "bg-white text-primary hover:bg-primary-100"
        } !h-auto cursor-pointer`}
        leadingIconContentClass="items-center gap-2"
        onCapsuleClick={handleShowDropdown}
        leadingIcon={
          <div
            className={`flex items-center justify-center w-6 h-6 rounded-full ${
              show ? "bg-white" : "bg-primary-100 group-hover:!bg-white"
            }`}
          >
            <AssignmentOutlinedIcon
              className="text-primary"
              style={{ width: "16px", height: "16px" }}
            />
          </div>
        }
        content={
          <label
            className="!mt-0 font-bold text-sm whitespace-nowrap cursor-pointer"
            htmlFor="btn"
          >
            {examName}
          </label>
        }
        trailingIcon={<ChevronDownIcon className="w-3 stroke-[4px]" />}
      />

      {show && (
        <div className="absolute flex flex-col gap-1 w-96 p-2 overflow-y-scroll mt-2 bg-white border border-gray-300 shadow-lg rounded-2xl">
          {examsHeadline && examsHeadline.length > 0 ? (
            examsHeadline.map((degree, index) => (
              <CommonAccordion
                key={index}
                accordionClass={{
                  boxShadow: "none",
                  padding: "4px",
                  borderRadius: "8px !important",
                  backgroundColor: "#F5F5F5",
                  "&:hover": {
                    backgroundColor: "#F9F9F9",
                  },
                  "&::before": { display: "none" },

                  "&.Mui-expanded": {
                    margin: "0px",
                  },
                }}
                summaryClass={{
                  alignItems: "center",
                  padding: "4px",
                  minHeight: "0px",

                  "& .MuiAccordionSummary-content": {
                    margin: "0px !important",
                  },
                  "&.Mui-expanded": {
                    minHeight: "0px",
                  },
                }}
                detailsClass={{
                  padding: "0px",
                }}
                summaryTypographyClass={{
                  fontWeight: "500",
                  fontSize: "12px",
                  color: "#5E5E5E",
                }}
                detailTypographyClass={{
                  fontWeight: "500",
                  fontSize: "12px",
                  color: "#5E5E5E",
                }}
                title={degree}
                children={
                  <ExamList
                    dropdownData={dropdownData}
                    degree={degree}
                    handleChooseExam={handleChooseExam}
                    selectedExamId={selectedExamId}
                  />
                }
                showSeparator={false}
                expand={true}
              />
            ))
          ) : (
            <div className="p-4 text-gray-500 text-center">
              No exams available
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export const ExamList = ({
  dropdownData,
  degree,
  handleChooseExam,
  selectedExamId,
}) => {
  return (
    <div className="flex flex-col w-full p-1 rounded-md bg-white">
      {dropdownData[degree]?.map((exam) => (
        <RadioButtonUI
          checkedIcon={<CheckCircleIcon className="w-6 text-primary" />}
          radioClass={`${
            selectedExamId === exam.id
              ? "text-primary bg-primary-50"
              : "text-black hover:bg-grey-50"
          }`}
          contentClass="text-sm font-bold"
          content={exam?.name}
          checked={selectedExamId === exam.id}
          onRadioClick={() => handleChooseExam(exam)}
        />
      ))}
    </div>
  );
};
